// Single source of truth for Free-Plan calculation limits.
// Imported by both the React stores and the Express routes so that
// reset/bonus/limit logic stays in lockstep on client and server.

export const FREE_PLAN_BASE_LIMIT = 25;

const sameMonth = (a, b) =>
  a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();

/**
 * True iff the monthly counter should reset before the next increment,
 * i.e. now is in a different month than the stored reset timestamp
 * (or no reset timestamp is recorded yet).
 */
export function isResetDue(profile, now = new Date()) {
  if (!profile?.calculations_reset_at) return true;
  return !sameMonth(now, new Date(profile.calculations_reset_at));
}

/**
 * Effective used-count for the current billing month.
 * Returns 0 when a month-boundary reset is due.
 */
export function calculationsUsed(profile, now = new Date()) {
  if (!profile) return 0;
  if (isResetDue(profile, now)) return 0;
  return profile.calculations_this_month || 0;
}

/**
 * Bonus calculations currently in effect (0 if missing, expired, or non-positive).
 */
export function activeBonus(profile, now = new Date()) {
  const amount = profile?.temporary_bonus_calculations;
  if (!amount || amount <= 0 || !profile?.bonus_valid_until) return 0;
  const validUntil = new Date(profile.bonus_valid_until);
  return now <= validUntil ? amount : 0;
}

/**
 * Monthly limit including any active bonus.
 * Returns Infinity for non-free plans, 0 for missing profile.
 */
export function effectiveLimit(profile, now = new Date()) {
  if (!profile) return 0;
  if (profile.plan !== 'free') return Infinity;
  return FREE_PLAN_BASE_LIMIT + activeBonus(profile, now);
}

/**
 * Snapshot of plan status. Use this whenever you need to decide whether
 * a user may calculate, or render a counter in the UI.
 *
 * Shape: { plan, total, used, remaining, bonus, canCalculate, isFree }
 * - total / remaining are Infinity for non-free plans.
 */
export function planStatus(profile, now = new Date()) {
  if (!profile) {
    return {
      plan: null,
      total: 0,
      used: 0,
      remaining: 0,
      bonus: 0,
      canCalculate: false,
      isFree: false
    };
  }
  const used = calculationsUsed(profile, now);
  const bonus = activeBonus(profile, now);
  const total = effectiveLimit(profile, now);
  const remaining = total === Infinity ? Infinity : Math.max(0, total - used);
  return {
    plan: profile.plan,
    total,
    used,
    remaining,
    bonus,
    canCalculate: profile.plan !== 'free' || used < total,
    isFree: profile.plan === 'free'
  };
}
